import { Component, input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { GetPowerAnalysisResponse } from '../../../../core/services/dashboard.service';

@Component({
    selector: 'app-power-analysis',
    standalone: true,
    imports: [CommonModule, ChartModule, SelectButtonModule, FormsModule],
    templateUrl: './power-analysis.component.html',
})
export class PowerAnalysisComponent {
    data = input<GetPowerAnalysisResponse | undefined>();
    selectedScope = signal('TOTAL');

    scopeOptions = computed(() => {
        const val = this.data();
        const options: any[] = [{ label: '總計', value: 'TOTAL' }];
        if (val?.totalUsage?.floors) {
            val.totalUsage.floors.forEach(f => {
                options.push({ label: f.floorName, value: f.floorId.toString() });
            });
        }
        return options;
    });

    onScopeChange(val: string) {
        this.selectedScope.set(val);
    }

    private baseChartOptions: any = {
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 16000,
                ticks: {
                    color: '#9ca3af',
                    callback: (value: any) => value.toLocaleString()
                },
                grid: {
                    color: '#e5e7eb',
                    drawBorder: false
                },
                border: { display: false }
            },
            x: {
                ticks: { color: '#4b5563', font: { weight: 'bold' } },
                grid: { display: false },
                border: { display: false }
            }
        },
        maintainAspectRatio: false
    };

    chartData = computed(() => {
        const data = this.data();
        if (!data || !data.totalUsage) {
            return { labels: ['上週同日全天', '今日累積'], datasets: [] };
        }
        
        let targetUsage;
        if (this.selectedScope() === 'TOTAL') {
            targetUsage = data.totalUsage.overall;
        } else {
            const floorId = parseInt(this.selectedScope(), 10);
            targetUsage = data.totalUsage.floors.find(f => f.floorId === floorId) || data.totalUsage.overall;
        }

        return {
            labels: ['上週同日全天', '今日累積'],
            datasets: [
                {
                    data: [targetUsage.lastWeekSameDayUsage, targetUsage.currentTotalUsage],
                    backgroundColor: ['#9ca3af', '#f59e0b'], // gray-400, amber-500
                    borderRadius: 8,
                    barThickness: 50
                }
            ]
        };
    });

    chartOptions = computed(() => {
        const data = this.data();
        if (!data || !data.totalUsage) return this.baseChartOptions;
        
        let targetUsage;
        if (this.selectedScope() === 'TOTAL') {
            targetUsage = data.totalUsage.overall;
        } else {
            const floorId = parseInt(this.selectedScope(), 10);
            targetUsage = data.totalUsage.floors.find(f => f.floorId === floorId) || data.totalUsage.overall;
        }

        const maxVal = Math.max(targetUsage.lastWeekSameDayUsage, targetUsage.currentTotalUsage) * 1.2;
        const computedMax = Math.ceil(maxVal / 1000) * 1000;

        return {
            ...this.baseChartOptions,
            scales: {
                ...this.baseChartOptions.scales,
                y: {
                    ...this.baseChartOptions.scales.y,
                    max: computedMax > 0 ? computedMax : 100
                }
            }
        };
    });

    currentRatio = computed(() => {
        const data = this.data();
        if (!data || !data.totalUsage) return 0;
        
        let targetUsage;
        if (this.selectedScope() === 'TOTAL') {
            targetUsage = data.totalUsage.overall;
        } else {
            const floorId = parseInt(this.selectedScope(), 10);
            targetUsage = data.totalUsage.floors.find(f => f.floorId === floorId) || data.totalUsage.overall;
        }
        return targetUsage.ratio;
    });
}
